import { auditMcpRenderAllowlist, formatRenderDriftFinding } from '../lib/mcp-render-allowlist-audit.js';

/**
 * ENG-5922 — `agt audit mcp-render` command.
 *
 * Walks `~/.augmented/<agent>/` directories on the host and reports
 * every sub-agent .md whose `tools:` allowlist is missing a wildcard
 * for an MCP server present in that agent's `.mcp.json`. Catches the
 * staleness Don found on his host (rendered allowlist diverged from
 * actual server set; trivially-correct sub-agent calls failed).
 *
 * Exit codes:
 *   0 — no findings (or some findings; we still exit 0 because the
 *       drift may be transient mid-poll-cycle)
 *   2 — only used with `--strict`. CI / post-deploy verification
 *       gates a deploy on a clean audit.
 *
 * Same shape as `agt audit subagents` (ENG-5897) and `agt audit
 * secrets` (ENG-5901). Three audit CLIs, three different invariants.
 */

interface AuditOptions {
  strict?: boolean;
  json?: boolean;
}

export async function auditMcpRenderCommand(options: AuditOptions = {}): Promise<void> {
  const findings = auditMcpRenderAllowlist();

  if (options.json) {
    process.stdout.write(`${JSON.stringify({ findings }, null, 2)}\n`);
  } else if (findings.length === 0) {
    process.stdout.write(
      'No render drift found. Every managed agent\'s sub-agent .md `tools:` allowlist covers every server in its `.mcp.json`.\n',
    );
  } else {
    process.stdout.write(
      `Found ${findings.length} sub-agent .md file${findings.length === 1 ? '' : 's'} with a stale tools allowlist:\n\n`,
    );
    for (const f of findings) {
      process.stdout.write(`  • ${f.agent}/${f.scope}/${f.subagent}.md\n`);
      process.stdout.write(`    missing: ${f.missing.join(', ')}\n`);
      process.stdout.write(`    file:    ${f.file}\n`);
    }
    process.stdout.write(
      '\nA missing wildcard means the sub-agent will get "No such tool available." on any call ' +
        'to that MCP server. The fix is to force a re-render (e.g. by adding/removing any MCP server, ' +
        'which routes through syncMcpToProject), OR restart the manager so the next poll picks up the ' +
        'live `.mcp.json` state. The root-cause write path that bypassed the render chokepoint is ' +
        'tracked in ENG-5922.\n',
    );
    for (const f of findings) {
      process.stderr.write(`${formatRenderDriftFinding(f)}\n`);
    }
  }

  if (options.strict && findings.length > 0) {
    process.exit(2);
  }
}
